import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button.tsx';
import {
  Calendar,
  Clock,
  Edit2,
  FileText,
  ListRestart,
  LoaderIcon,
  Plus,
  Power,
  X,
} from 'lucide-react';
import { cn } from '@/utils/utils.ts';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { AddScheduleModal } from '@/features/plants/components/plant-schedule/file-tab/add-schedule-modal.tsx';
import { RenameScheduleModal } from '@/features/plants/components/plant-schedule/file-tab/rename-schedule-modal.tsx';
import { DeleteScheduleModal } from '@/features/plants/components/plant-schedule/file-tab/delete-schedule-modal.tsx';
import { WeeklyScheduleEditor } from '@/features/plants/components/plant-schedule/schedule/components/weekly-schedule-editor.tsx';
import {
  PowerSchedule,
  WeeklySchedule,
} from '@/features/plants/schemas/schedule-plant.schema.ts';
import { Badge } from '@/components/ui/badge.tsx';
import { ActivateScheduleModal } from '@/features/plants/components/plant-schedule/file-tab/activate-schedule-modal.tsx';
import { useDeleteSchedulePlantMutation } from '@/features/plants/hooks/mutations/[plantId]/schedule/[scheduleId]/use-delete-schedule.ts';
import { useUpdateSchedulePlantMutation } from '@/features/plants/hooks/mutations/[plantId]/schedule/[scheduleId]/use-update-schedule.ts';
import { useCreateSchedulePlantMutation } from '@/features/plants/hooks/mutations/[plantId]/schedule/use-create-schedule.ts';
import { ScheduleRevision } from '@/features/plants/components/plant-schedule/revision/schedule-revisions.tsx';
import { TPlantControlSchedulerRevision } from '@/features/plants/schemas/schedule-plant-revision.schema';
import { ActionTooltip } from '@/components/ui/action-tooltip';

interface FileTabProps {
  plantId?: string;
  maxTabs?: number;
  initialSchedules?: PowerSchedule[];
  onScheduleSelect?: (schedule: PowerSchedule | null) => void;
  isLoading?: boolean;
}

export function FileTab({
  plantId,
  maxTabs = 3,
  initialSchedules = [],
  onScheduleSelect,
  isLoading,
}: FileTabProps) {
  const [schedules, setSchedules] = useState<PowerSchedule[]>(initialSchedules);
  const [activeScheduleId, setActiveScheduleId] = useState<string | null>(
    initialSchedules.length > 0 ? initialSchedules[0].scheduleId : null
  );

  const { mutate: deleteSchedulePlantMutation, isPending: isDeleting } =
    useDeleteSchedulePlantMutation({ plantId });
  const { mutate: updateSchedulePlantMutation, isPending: isUpdating } =
    useUpdateSchedulePlantMutation({
      plantId,
    });
  const { mutate: createSchedulePlantMutation, isPending: isCreating } =
    useCreateSchedulePlantMutation({
      plantId,
    });

  useEffect(() => {
    if (!initialSchedules?.length) return;

    setSchedules(initialSchedules);
    if (!activeScheduleId) {
      setActiveScheduleId(initialSchedules[0]?.scheduleId);
    }

    const isCurrentScheduleIdAvailable = initialSchedules.find(
      (s) => s.scheduleId === activeScheduleId
    )?.scheduleId;

    if (!isCurrentScheduleIdAvailable) {
      setActiveScheduleId(initialSchedules[0].scheduleId);
    }
  }, [initialSchedules]);

  const currentSchedule = schedules.find((s) => s.scheduleId === activeScheduleId);

  const handleAddSchedule = (scheduleName: string) => {
    const newSchedule: PowerSchedule = {
      newlyAdded: true,
      wasEdited: true,
      plantId: '',
      companyId: '',
      scheduleId: crypto.randomUUID(),
      scheduleName: scheduleName?.trim(),
      weeklySchedule: {
        Monday: [],
        Tuesday: [],
        Wednesday: [],
        Thursday: [],
        Friday: [],
        Saturday: [],
        Sunday: [],
      },
      currentStatus: 'stopped',
      isActive: !schedules?.find((s) => s.isActive)?.isActive,
      version: 1,
    };
    setSchedules([...schedules, newSchedule]);
    handleTabClick(newSchedule);
  };

  const handleSetWeeklySchedule = (weeklySchedule: WeeklySchedule) => {
    const updatedSchedules = schedules.map((schedule) =>
      schedule.scheduleId === activeScheduleId
        ? { ...schedule, wasEdited: true, weeklySchedule }
        : schedule
    );

    setSchedules(updatedSchedules);
  };

  const handleDeleteSchedule = (scheduleId: string) => {
    const deletedSchedule = schedules.find((s) => s.scheduleId === scheduleId);

    const updatedSchedules = schedules.filter((s) => s.scheduleId !== scheduleId);

    setSchedules(updatedSchedules);

    const activeStillExists = updatedSchedules.some((s) => s.scheduleId === activeScheduleId);

    if (!activeStillExists) {
      if (updatedSchedules.length > 0) {
        handleTabClick(updatedSchedules[0]);
      } else {
        handleTabClick(null);
      }
    }

    if (deletedSchedule && deletedSchedule.newlyAdded) return;
    deleteSchedulePlantMutation({ scheduleId });
  };

  const handleRenameSchedule = (scheduleId: string, newName: string) => {
    const updatedSchedules = schedules.map((schedule) =>
      schedule.scheduleId === scheduleId
        ? { ...schedule, wasEdited: true, scheduleName: newName.trim() }
        : schedule
    );

    setSchedules(updatedSchedules);

    if (scheduleId === activeScheduleId) {
      const renamedSchedule = updatedSchedules.find((s) => s.scheduleId === scheduleId);
      if (renamedSchedule) {
        onScheduleSelect?.(renamedSchedule);
      }
    }
  };

  const handleTabClick = (schedule: PowerSchedule | null) => {
    if (!schedule) {
      setActiveScheduleId(null);
      onScheduleSelect?.(null);
      return;
    }

    setActiveScheduleId(schedule.scheduleId);
    onScheduleSelect?.(schedule);
  };

  const handleActivateSchedule = (scheduleId: string, activate: boolean) => {
    const otherActiveSchedule = schedules.find(
      (s) => s.isActive && s.scheduleId !== scheduleId
    );
    if (activate && otherActiveSchedule) return;

    const updatedSchedules = schedules.map((schedule) =>
      schedule.scheduleId === scheduleId
        ? { ...schedule, wasEdited: true, isActive: activate }
        : schedule
    );

    setSchedules(updatedSchedules);
  };

  const onUpdateCreateSchedule = () => {
    if (currentSchedule?.newlyAdded) {
      createSchedulePlantMutation({ schedule: currentSchedule });

      return;
    }

    updateSchedulePlantMutation({ schedule: currentSchedule });
  };

  const onResetClick = () => {
    setSchedules(initialSchedules);
  };

  const onReplaceSchedule = (scheduleRevision: TPlantControlSchedulerRevision) => {
    const snapshot = scheduleRevision.snapshot;
    if (!snapshot) return;

    const areActiveSchedules = initialSchedules.find((schedules) => schedules.isActive);

    setSchedules((previousSchedules) => {
      return previousSchedules.map((schedule) => {
        if (schedule.scheduleId !== scheduleRevision.scheduleId) {
          return schedule;
        }

        return {
          ...schedule,
          wasEdited: true,
          currentStatus: areActiveSchedules ? 'stopped' : snapshot.currentStatus,
          isActive: areActiveSchedules ? false : snapshot.isActive,
          scheduleName: snapshot.scheduleName,
          weeklySchedule: snapshot.weeklySchedule,
          description: snapshot.description ?? schedule.description,
        };
      });
    });
  };

  useEffect(() => {
    {
      const availableSchedule = schedules.find(
        (schedule) => schedule.scheduleId === activeScheduleId
      );

      if (availableSchedule) {
        handleTabClick(availableSchedule);
        return;
      }

      if (schedules.length > 0) {
        handleTabClick(schedules[0]);
        return;
      }
      handleTabClick(null);
    }
  }, [activeScheduleId, schedules]);

  if (schedules.length === 0 || isLoading) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-between rounded-t-lg border-b border-gray-200/60 bg-gray-50/50">
          <AddScheduleModal
            maxTabs={maxTabs}
            currentTabCount={schedules.length}
            onAddSchedule={handleAddSchedule}
          >
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-3 text-gray-600 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-800"
            >
              <Plus className="mr-1 h-4 w-4" />
              Add Schedule
            </Button>
          </AddScheduleModal>
        </div>

        <div className="flex flex-col gap-4">
          <DashboardCard
            isLoading={isLoading}
            className="min-h-[672px] rounded-t-none border border-t-0 border-gray-200/60 p-6"
          >
            <div className="flex h-full flex-1 items-center justify-center">
              <div className="max-w-md space-y-4 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                  <FileText className="h-8 w-8 text-gray-400" />
                </div>
                <div>
                  <h3 className="mb-2 text-lg font-semibold text-gray-900">
                    No Schedules Yet
                  </h3>
                  <p className="mb-4 text-sm text-gray-500">
                    Create the first plant schedule. You can set time windows and configure
                    automated sending.
                  </p>
                  <AddScheduleModal
                    maxTabs={maxTabs}
                    currentTabCount={schedules.length}
                    onAddSchedule={handleAddSchedule}
                  >
                    <Button className="mt-2">
                      <Plus className="mr-2 h-4 w-4" />
                      Create First Schedule
                    </Button>
                  </AddScheduleModal>
                </div>
              </div>
            </div>
          </DashboardCard>
          {isLoading && (
            <DashboardCard
              isLoading={isLoading}
              className="min-h-[300px] border-gray-200/60"
            ></DashboardCard>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="w-full">
        <div className="flex items-center rounded-t-lg border-b">
          <div className="scrollbar-hide flex items-center space-x-0.5 overflow-x-auto">
            {schedules.map((schedule) => (
              <div
                data-tab={schedule.scheduleId}
                key={schedule.scheduleId}
                className={cn(
                  'group relative flex h-8 max-w-[250px] min-w-0 cursor-pointer items-center rounded-t-md px-3 transition-all duration-200 ease-out',
                  activeScheduleId === schedule.scheduleId
                    ? 'border border-b-0 border-gray-200/60 bg-white text-gray-900'
                    : 'border border-b-0 border-[#F9FAFB] text-gray-600 hover:bg-white/60 hover:text-gray-800'
                )}
                onClick={() => handleTabClick(schedule)}
              >
                <div className="flex min-w-0 flex-1 items-center space-x-2">
                  <Clock className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                  <span className="truncate text-sm font-medium">{schedule.scheduleName}</span>
                </div>

                <div className="ml-1 flex items-center space-x-1">
                  <RenameScheduleModal
                    schedule={schedule}
                    onRenameSchedule={handleRenameSchedule}
                  >
                    <Button
                      data-tab-button="rename-button"
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'h-5 w-5 p-0 opacity-0 transition-opacity duration-150 group-hover:opacity-100 hover:bg-gray-100',
                        activeScheduleId === schedule.scheduleId &&
                          'opacity-60 hover:opacity-100'
                      )}
                      title="Rename schedule"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                  </RenameScheduleModal>
                  <DeleteScheduleModal
                    schedule={schedule}
                    onDeleteSchedule={() => handleDeleteSchedule(schedule.scheduleId)}
                  >
                    <Button
                      data-tab-button="delete-button"
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'h-5 w-5 p-0 opacity-0 transition-opacity duration-150 group-hover:opacity-100 hover:bg-gray-100',
                        activeScheduleId === schedule.scheduleId &&
                          'opacity-60 hover:opacity-100'
                      )}
                      title="Delete schedule"
                      disabled={isDeleting}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </DeleteScheduleModal>
                </div>
              </div>
            ))}
          </div>

          <AddScheduleModal
            maxTabs={maxTabs}
            currentTabCount={schedules.length}
            onAddSchedule={handleAddSchedule}
          >
            <Button
              variant="ghost"
              size="sm"
              className="ml-2 h-8 w-8 p-0 text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700"
              title="Add new schedule"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </AddScheduleModal>
        </div>

        <DashboardCard
          isLoading={isLoading}
          className="@container min-h-[672px] rounded-t-none border border-t-0 border-gray-200/60 p-1 py-4 sm:p-6"
        >
          <div className="flex h-full flex-col gap-4 text-gray-500 sm:gap-6">
            <div className="mx-2 flex justify-between gap-2 @min-[540px]:justify-center">
              <div className="flex items-center gap-2 sm:self-center">
                <Calendar className="h-5 w-5 text-gray-300" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {currentSchedule?.scheduleName}
                  </h3>
                </div>
              </div>
              <Badge variant={currentSchedule?.isActive ? 'green' : 'red'}>
                {currentSchedule?.isActive ? 'Active Schedule' : 'Inactive Schedule'}
              </Badge>
            </div>
            <WeeklyScheduleEditor
              weeklySchedule={
                currentSchedule?.weeklySchedule || {
                  Monday: [],
                  Tuesday: [],
                  Wednesday: [],
                  Thursday: [],
                  Friday: [],
                  Saturday: [],
                  Sunday: [],
                }
              }
              onChangeWeeklySchedule={handleSetWeeklySchedule}
              className="self-start"
            />
            <div className="flex justify-end gap-2">
              <ActionTooltip
                asChild
                align="end"
                alignOffset={-24}
                title={`Replace with the latest version`}
                clickedTitle={`Replaced with the latest version`}
                onClick={onResetClick}
              >
                <Button
                  variant="secondary"
                  disabled={isCreating || isUpdating}
                  onClick={onResetClick}
                  className="p-1 px-1.5"
                >
                  <ListRestart className="h-6 w-6" />
                </Button>
              </ActionTooltip>

              <ActivateScheduleModal
                currentSchedule={currentSchedule}
                initialSchedules={initialSchedules}
                schedules={schedules}
                onActivateSchedule={handleActivateSchedule}
              >
                <Button
                  disabled={isCreating || isUpdating}
                  className={cn(
                    'p-3 transition-all duration-200',
                    currentSchedule?.isActive
                      ? 'bg-red-600 text-white hover:bg-red-600/70'
                      : 'bg-green-600 text-white hover:bg-green-600/90'
                  )}
                >
                  <Power className="h-4 w-4" />
                </Button>
              </ActivateScheduleModal>

              <Button
                onClick={() => onUpdateCreateSchedule()}
                disabled={!currentSchedule?.wasEdited || isUpdating || isCreating}
                className={cn('transition-all duration-200')}
              >
                {(isUpdating || isCreating) && (
                  <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
                )}
                {currentSchedule?.newlyAdded ? 'Create' : 'Update'}
              </Button>
            </div>
          </div>
        </DashboardCard>
      </div>
      {!currentSchedule?.newlyAdded && (
        <ScheduleRevision
          plantId={plantId}
          scheduleId={activeScheduleId ?? undefined}
          onReplaceSchedule={onReplaceSchedule}
        />
      )}
    </div>
  );
}
